import { IQuickFind } from '@ifit/gateway-wolf-dashboard-service';
import QuickFindItem from './quick-find-item';
import { useQuickFinds } from '../../state-management/server-state/use-quick-finds';
import RowHeading from '../row-heading';
import CardSkeleton from '@ifit/mp-card-skeleton';
import { isRestrictedUserType } from '../../util/is-restricted-user-type';
import { isStrengthMachine } from '../../util/is-strength';
import { useEquipmentType } from '../../state-management/selectors/use-equipment-type';
import { isClub } from '../../util/is-club';
import { useSoftwareNumber } from '../../state-management/selectors/use-software-number';
import { isMatrix } from '../../util/is-matrix';
import { useSupportsDeepLinksV1 } from '../../state-management/selectors/use-supports-deep-links-v1';
import { useUserType } from '../../state-management/selectors/use-user-type';
import { Text } from './text';
import { useMatrixCommercial } from '../../hooks/use-matrix-commercial';
import './index.scss';

const skeletonCards = Array(10).fill(0);

export default function QuickFindsRow(): JSX.Element {
  const softwareNumber = useSoftwareNumber();
  const equipmentType = useEquipmentType();
  const supportsDeepLinksV1 = useSupportsDeepLinksV1();
  const userType = useUserType();
  const isMatrixCommerical = useMatrixCommercial();

  const isRestrictedUser = isRestrictedUserType(userType, isClub(softwareNumber), isMatrix(softwareNumber));
  const isStrength = isStrengthMachine(equipmentType);
  const shouldDisableCall = isRestrictedUser || isStrength || !supportsDeepLinksV1;

  const { isLoading, isFetching, error, data, isSuccess } = useQuickFinds(shouldDisableCall);

  const hideRow = shouldHideRow({ data, error, isSuccess, shouldDisableCall, isMatrixCommerical });

  if (hideRow) {
    return null;
  }

  let content: JSX.Element[];
  if (isLoading) {
    content = skeletonCards.map((_, index) => <CardSkeleton key={index} className="custom" />);
  } else {
    content = data.map((item) => <QuickFindItem key={item.title} item={item} />);
  }
  return (
    <div className="row-category">
      <RowHeading title={Text.title} fetching={isFetching} />
      <div className="cards" style={{ ['--cards-per-row' as any]: content.length }}>
        {content}
      </div>
    </div>
  );
}

interface IHideRowContext {
  data: IQuickFind[];
  error: Error;
  isSuccess: boolean;
  shouldDisableCall: boolean;
  isMatrixCommerical: boolean;
}

function shouldHideRow(rowContext: IHideRowContext): boolean {
  const { data, shouldDisableCall, error, isSuccess, isMatrixCommerical } = rowContext;

  const noValidData = !data || data.length === 0;
  const successWithNoData = noValidData && isSuccess;
  const errorWithNoCachedData = error && noValidData;
  return Boolean(shouldDisableCall || errorWithNoCachedData || successWithNoData || isMatrixCommerical);
}
